import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  computed,
  linkedSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { toSignal } from '@angular/core/rxjs-interop';
import { PersonType } from 'src/app/services/models/enums';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { GenericTableComponent } from 'src/app/shared/components/generic-table';
import { TableCellDirective } from 'src/app/shared/components/generic-table/directives/table-cell.directive';
import { TableExpandableContentDirective } from 'src/app/shared/components/generic-table/directives/table-expandable-content.directive';
import {
  getWalletAlertReportTableConfig,
  WALLET_ALERT_REPORT_NO_DATA_MESSAGE,
} from './wallet-alert-report-table-config';
import { WalletAlertReportFacade } from './wallet-alert-report.facade';
import {
  AlertIndicatorVariation,
  FlattenedIndicator,
  RelationshipLevel,
  WalletRegistryAlertResponse,
} from './models/wallet-alert-report.models';
import {
  ALERT_TABLE_TITLES,
  getRelationshipLevelSuffix,
} from './models/wallet-alert-report.constants';
import { AlertIndicatorsTagsComponent } from './alert-indicators-tags/alert-indicators-tags.component';
import { CarouselComponent } from 'src/app/shared/components/carousel/carousel.component';
import { AlertVariationCardComponent } from './app-alert-variation-card/alert-variation-card.component';
import { SearchInputComponent } from 'src/app/shared/components/search-input/search-input.component';
import { getDocumentMask, validateBrazilianDocument } from 'src/app/helpers/documents-utils';
import { Wallet } from 'src/app/services/models/wallet-service/wallet.model';
import { WalletSelectComponent } from 'src/app/shared/components/wallet-select/wallet-select.component';
import { RegistrationDataLoaderService } from './services/registration-data-loader.service';
import { IndicatorGroup } from 'src/app/model/configuration-service.models';
import {
  PartnerAlertsDialogComponent,
  PartnerAlertsDialogData,
} from './partner-alerts-dialog/partner-alerts-dialog.component';
import {
  RegistrationDataAlertsDialogComponent,
  RegistrationDataAlertsDialogData,
} from './registration-data-alerts-dialog/registration-data-alerts-dialog.component';

@Component({
  selector: 'app-wallet-alert-report',
  templateUrl: './wallet-alert-report.component.html',
  styleUrls: ['./wallet-alert-report.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    SharedModule,
    IconComponent,
    GenericTableComponent,
    TableCellDirective,
    TableExpandableContentDirective,
    AlertIndicatorsTagsComponent,
    AlertVariationCardComponent,
    CarouselComponent,
    SearchInputComponent,
    WalletSelectComponent,
  ],
  providers: [WalletAlertReportFacade, RegistrationDataLoaderService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalletAlertReportComponent implements OnDestroy {
  readonly facade = inject(WalletAlertReportFacade);
  private readonly globalService = inject(GlobalServiceService);
  private readonly dialog = inject(MatDialog);
  private readonly registrationDataLoader = inject(RegistrationDataLoaderService);

  readonly selectedIndicator = linkedSignal<FlattenedIndicator | undefined>(
    () => this.indicators()[0],
  );
  selectedWallet?: Wallet;
  urlAssets: string = localStorage.getItem('urlAssets') || '';
  searchedDocument = '';

  readonly tableConfig = getWalletAlertReportTableConfig();
  noDataMessage: string = WALLET_ALERT_REPORT_NO_DATA_MESSAGE;
  tableTitles = ALERT_TABLE_TITLES;

  readonly personType = toSignal(this.globalService.getPersonType$(), {
    initialValue: this.globalService.getPersonTypeCurrentValue(),
  });

  readonly indicators = this.facade.indicators;
  readonly alerts = this.facade.alerts;
  readonly lastProcessingDate = this.facade.lastProcessingDate;
  readonly pagination = this.facade.pagination;
  readonly isLoading = this.facade.isLoading;
  readonly isIndicatorSelectDisabled = this.facade.isIndicatorSelectDisabled;
  readonly hasError = this.facade.hasError;
  readonly hasNoIndicators = this.facade.hasNoIndicators;
  readonly isLoadingIndicators = this.facade.isLoadingIndicators;

  readonly documentInputSearchLabel = computed(
    () => $localize`:@@search_document:Buscar documento`,
  );

  readonly documentSearchMask = computed(() => getDocumentMask(this.personType()));

  readonly documentValidator = validateBrazilianDocument;

  readonly selectOptionImage = computed(() => {
    const isCompany = this.personType() === PersonType.COMPANY;
    return isCompany
      ? `${this.urlAssets}/icons/carteras-juridicas-estatica.png`
      : `${this.urlAssets}/icons/carteras-estaticas.png`;
  });

  ngOnDestroy(): void {
    this.facade.reset();
  }

  onWalletSelect(event: MatSelectChange): void {
    this.selectedWallet = event.value;
    this.selectedIndicator.set(undefined);

    if (this.selectedWallet?.planId) {
      this.facade.loadIndicators(this.selectedWallet.planId, this.selectedWallet.type, () =>
        this.onIndicatorsLoaded(),
      );
    }
  }

  onIndicatorsSelect(event: MatSelectChange): void {
    this.selectedIndicator.set(event.value);
    this.loadAlerts(0);
  }

  onIndicatorsLoaded(): void {
    const indicators = this.indicators();
    if (indicators.length === 0 || !this.selectedWallet) return;

    const isLoadingOrEmpty = indicators[0].id === 'loading' || indicators[0].id === 'no-indicators';
    if (!isLoadingOrEmpty && indicators.length > 1) {
      this.loadAlerts(0);
    }
  }

  onSearchTriggered(): void {
    this.loadAlerts(0);
  }

  changePage(page: number): void {
    this.loadAlerts(page);
  }

  isParentIndicator(indicator: FlattenedIndicator): boolean {
    return this.facade.isParentIndicator(indicator);
  }

  getIndicatorOptionName(indicator: FlattenedIndicator): string {
    if (this.isParentIndicator(indicator)) return indicator.name;
    return indicator.name + getRelationshipLevelSuffix(indicator.relationshipLevel);
  }

  onPartnerCardClick(cardData: AlertIndicatorVariation, alert: WalletRegistryAlertResponse): void {
    if (cardData.isRegistrationData) {
      this.openRegistrationDataDialog(cardData, alert);
      return;
    }
    if (cardData.isPartnerRegistrationData) {
      this.openPartnerRegistrationDataDialog(cardData, alert);
      return;
    }
    if (cardData.isPartnerShareholdingData) {
      this.openShareholdingDialog(cardData, alert);
      return;
    }
    this.openPartnerAlertsDialog(cardData, alert);
  }

  private loadAlerts(page: number = 0): void {
    const indicator = this.selectedIndicator();
    if (!this.selectedWallet || !indicator) return;

    this.facade.loadAlerts({
      wallet: this.selectedWallet,
      indicator,
      document: this.searchedDocument,
      page,
    });
  }

  private openRegistrationDataDialog(
    cardData: AlertIndicatorVariation,
    alert: WalletRegistryAlertResponse,
  ): void {
    if (!this.selectedWallet) return;
    const wallet = this.selectedWallet;
    const data: RegistrationDataAlertsDialogData = {
      cardData,
      registryDocument: alert.document ?? '',
    };
    const dialogRef = this.dialog.open(RegistrationDataAlertsDialogComponent, {
      data,
      width: '700px',
      maxHeight: '700px',
    });
    this.registrationDataLoader
      .loadRegistrationData({
        walletId: wallet.id,
        registryId: alert.registryId,
        type: wallet.type as any,
      })
      .subscribe({
        next: (attributes) => {
          const rows = attributes.map((attr) => ({
            label: attr.label || '-',
            value: attr.value || '-',
          }));
          dialogRef.componentInstance.resolveRegistrationData(rows);
        },
        error: () => {
          dialogRef.componentInstance.resolveRegistrationData([]);
        },
      });
  }

  private openPartnerRegistrationDataDialog(
    cardData: AlertIndicatorVariation,
    alert: WalletRegistryAlertResponse,
  ): void {
    const relevantPartnerAlerts = (alert.partnerAlerts ?? []).filter(
      (a) =>
        a.relationshipLevel === cardData.relationshipLevel &&
        a.indicatorGroup === cardData.indicatorGroup,
    );
    const partners = this.facade.buildPartnersWithFlags(relevantPartnerAlerts);
    const level = cardData.relationshipLevel as RelationshipLevel.L1 | RelationshipLevel.L2;

    const data: PartnerAlertsDialogData = {
      cardData,
      partnerAlerts: [],
      registryDocument: alert.document ?? '',
    };
    const dialogRef = this.dialog.open(PartnerAlertsDialogComponent, {
      data,
      width: '700px',
      maxHeight: '700px',
    });
    this.facade.loadPartnersRegistrationData(level, partners).subscribe((registrationData) => {
      const rows =
        registrationData === null
          ? null
          : registrationData.map((r) => ({
              document: r.document,
              label: r.label,
              value: r.value,
            }));
      dialogRef.componentInstance.resolveRegistrationData(rows);
    });
  }

  private openShareholdingDialog(
    cardData: AlertIndicatorVariation,
    alert: WalletRegistryAlertResponse,
  ): void {
    const shareholdingAlert = (alert.alerts ?? []).find(
      (a) =>
        a.indicatorGroup === IndicatorGroup.PERCENTUAL_QUADRO_SOCIETARIO &&
        a.relationshipLevel === cardData.relationshipLevel,
    );
    const partnerRegistryIds = this.facade.parseShareholdingPartnerIds(
      shareholdingAlert?.currentValue,
    );
    const rootRegistryId = alert.registryId ?? '';
    const level = cardData.relationshipLevel as RelationshipLevel.L1 | RelationshipLevel.L2;

    const data: PartnerAlertsDialogData = {
      cardData,
      partnerAlerts: [],
      registryDocument: alert.document ?? '',
    };
    const dialogRef = this.dialog.open(PartnerAlertsDialogComponent, {
      data,
      width: '700px',
      maxHeight: '700px',
    });
    this.facade
      .loadPartnersShareholding(rootRegistryId, level, partnerRegistryIds)
      .subscribe((shareholding) => {
        const rows =
          shareholding === null
            ? null
            : shareholding.map((s) => ({
                document: s.document ?? '',
                percentage: s.shareholdingPercentage ?? null,
              }));
        dialogRef.componentInstance.resolveShareholding(rows);
      });
  }

  private openPartnerAlertsDialog(
    cardData: AlertIndicatorVariation,
    alert: WalletRegistryAlertResponse,
  ): void {
    const data: PartnerAlertsDialogData = {
      cardData,
      partnerAlerts: (alert.partnerAlerts ?? []).filter(
        (a) =>
          a.indicatorGroup === cardData.indicatorGroup &&
          a.relationshipLevel === cardData.relationshipLevel,
      ),
      registryDocument: alert.document ?? '',
    };
    this.dialog.open(PartnerAlertsDialogComponent, { data, width: '700px', maxHeight: '700px' });
  }
}
